import React, { useState } from "react";
import { FiEye, FiEyeOff } from "react-icons/fi";
import bg from "../../assets/first_bg.png";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../config/apiConfig";
import { toast } from "sonner";
import SignInWithGoogle from "../../components/GoogleSignIn";

const LoginPage = () => {
    const [formData, setFormData] = useState({ identifier: "", password: "" });
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    const navigate = useNavigate();

    const togglePassword = () => setShowPassword(!showPassword);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        if (error) setError("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError("");
        const { identifier, password } = formData;

        if (!identifier || !password) {
            toast.error("Please enter your name (or email) and password.");
            setIsLoading(false);
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/users/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ identifier, password }),
            });

            const data = await response.json();

            if (response.ok) {
                localStorage.setItem("token", data.token);
                localStorage.setItem("refreshToken", data.refreshToken);
                localStorage.setItem("fullName", data.fullName);
                localStorage.setItem("hasSeenModal", data.hasSeenModal);
                localStorage.setItem("accountType", data.accountType);
                // Pick based on actual accountType — not first non-null —
                // so a teacher/parent never sees a stale TSA-* if both columns are populated.
                const roleId =
                    data.accountType === "teacher" ? data.teacher_id :
                    data.accountType === "parent"  ? data.parent_id  :
                    data.student_id;
                if (roleId) localStorage.setItem("role_id", roleId);

                // Show the role ID on first login so the user writes it down
                const roleLabel =
                    data.accountType === "teacher" ? "Teacher ID" :
                    data.accountType === "parent"  ? "Parent ID"  : "Student ID";

                if (!data.hasSeenModal && roleId) {
                    toast.success(
                        `Welcome, ${data.fullName}! Your ${roleLabel} is ${roleId} — write it down, you can use it to log in!`,
                        { duration: 8000 }
                    );
                }

                // Auto-join teacher if student arrived via an invite link
                const pendingTeacherCode = localStorage.getItem("pending_teacher_code");
                if (data.accountType === "student" && pendingTeacherCode) {
                    try {
                        const joinRes = await fetch(`${API_BASE_URL}/api/teachers/join/${pendingTeacherCode}`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json", Authorization: `Bearer ${data.token}` },
                        });
                        if (joinRes.ok) localStorage.removeItem("pending_teacher_code");
                        else console.error("Auto-join teacher error:", await joinRes.json());
                    } catch (e) {
                        console.error("Auto-join teacher failed:", e);
                    }
                }

                // Auto-join parent if student arrived via a parent invite link
                const pendingParentCode = localStorage.getItem("pending_parent_code");
                if (data.accountType === "student" && pendingParentCode) {
                    try {
                        const joinRes = await fetch(`${API_BASE_URL}/api/parents/join/${pendingParentCode}`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json", Authorization: `Bearer ${data.token}` },
                        });
                        if (joinRes.ok) localStorage.removeItem("pending_parent_code");
                        else console.error("Auto-join parent error:", await joinRes.json());
                    } catch (e) {
                        console.error("Auto-join parent failed:", e);
                    }
                }

                if (!data.hasSeenModal || data.accountType !== "student") {
                    toast.success("Logged in successfully");
                }

                navigate(
                    data.accountType === "parent"
                        ? "/parent-dashboard"
                        : data.accountType === "teacher"
                            ? "/teacher-dashboard"
                            : data.onboardingCompleted
                                ? "/dashboard"
                                : "/onboarding"
                );

            } else {
                toast.error(data.message || "Login failed.");
                setError(data.message);
            }
        } catch (error) {
            console.error("Login error:", error);
            toast.error("An error occurred. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div
            className="flex justify-center items-center min-h-screen"
            style={{
                backgroundImage: `url(${bg})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                backgroundColor: "#2148C0",
            }}
        >
            <div className="flex flex-col md:flex-row bg-transparent rounded-lg overflow-hidden w-full max-w-4xl">
                <div className="w-[80%] m-auto md:w-1/2 p-8 flex flex-col justify-center bg-opacity-80 rounded-lg">
                    <h2 className="text-3xl font-bold text-center mb-6 text-white">TeeChaa</h2>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Identifier — email, role ID, or phone number */}
                        <div>
                            <input
                                type="text"
                                name="identifier"
                                placeholder="Email, Student ID, or Phone number"
                                value={formData.identifier}
                                onChange={handleChange}
                                className="w-full p-2 border rounded-lg focus:ring focus:ring-white bg-transparent text-white placeholder-gray-300"
                                required
                            />
                            <p className="text-xs text-white/60 mt-1 pl-1">
                                e.g. you@email.com · TSA-847291 · 08012345678
                            </p>
                        </div>

                        {/* Password Field */}
                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                name="password"
                                placeholder="PASSWORD"
                                value={formData.password}
                                onChange={handleChange}
                                className="w-full p-2 border rounded-lg focus:ring focus:ring-blue-300 bg-transparent text-white placeholder-gray-300"
                                required
                            />
                            <span
                                onClick={togglePassword}
                                className="absolute right-3 top-3.5 cursor-pointer text-white"
                            >
                                {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                            </span>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <p className="text-sm text-red-300 bg-white/10 px-3 py-2 rounded">{error}</p>
                        )}

                        {/* Submit Button */}
                        <button
                            type="submit"
                            className="w-full bg-white text-[#2148C0] p-2 rounded-lg hover:bg-gray-200 transition duration-300"
                        >
                            {isLoading ? (
                                <svg
                                    className="animate-spin h-5 w-5 text-[#2148C0] m-auto"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    ></circle>
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                    ></path>
                                </svg>
                            ) : (
                                "LOGIN"
                            )}
                        </button>
                    </form>

                    {/* Forgot Password */}
                    <p
                        onClick={() => navigate("/forgot-password")}
                        className="w-full text-end mt-2 text-white hover:underline cursor-pointer"
                    >
                        Forgot Password?
                    </p>

                    {/* Separator */}
                    <div className="flex items-center my-5">
                        <div className="flex-1 h-px bg-gray-300"></div>
                        <span className="px-3 text-gray-300">OR</span>
                        <div className="flex-1 h-px bg-gray-300"></div>
                    </div>

                    {/* Google Sign-in */}
                    <div className="w-full">
                        <SignInWithGoogle />
                    </div>

                    {/* Sign Up Link */}
                    <p className="mt-4 text-center text-gray-300">
                        Don't have an account?{" "}
                        <a href="/signup" className="text-white hover:underline">
                            Sign Up
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
